import { z } from 'zod'
import i18n from '@/i18n/config'

// Re-export memory constants from agent.ts to avoid duplication
export { MEMORY_TYPES, MEMORY_SCOPES } from './agent'
export type { MemoryType, MemoryScope } from './agent'

// Available LLM providers and their models
export const LLM_PROVIDERS = {
  openai: {
    label: 'OpenAI',
    models: [
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      { value: 'gpt-4', label: 'GPT-4' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    ],
  },
  anthropic: {
    label: 'Anthropic',
    models: [
      { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
      { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
      { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
      { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    ],
  },
  google: {
    label: 'Google',
    models: [
      { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
      { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
      { value: 'gemini-pro', label: 'Gemini Pro' },
    ],
  },
} as const

export type LLMProvider = keyof typeof LLM_PROVIDERS

// Agent icons (emoji presets)
export const AGENT_ICONS = [
  '🤖', '🧠', '💡', '⚡', '🎯', '🔮', '🌟', '🚀',
  '💼', '📊', '📝', '🔍', '💬', '🎨', '🛠️', '📚',
  '🌐', '🔒', '📈', '🎪', '🏆', '💎', '🔥', '✨',
] as const

// Step 1: Basic Info Schema

// Factory function to get step basic info schema with current translations
export const getStepBasicInfoSchema = () => z.object({
  name: z
    .string()
    .min(2, i18n.t('validation:name.minLength', { min: 2 }))
    .max(255, i18n.t('validation:name.maxLength', { max: 255 })),
  description: z
    .string()
    .max(2000, i18n.t('validation:description.maxLength', { max: 2000 }))
    .optional()
    .nullable(),
  icon: z.string().default('🤖'),
})

// Static schema for backwards compatibility
export const stepBasicInfoSchema = getStepBasicInfoSchema()

export type StepBasicInfoData = z.infer<typeof stepBasicInfoSchema>

// Step 2: LLM Config Schema

// Factory function to get step LLM config schema with current translations
export const getStepLLMConfigSchema = () => z.object({
  provider: z.string().optional().nullable(),
  model: z.string().optional().nullable(),
  temperature: z.number().min(0).max(2).default(0.7),
  max_tokens: z.number().min(1).max(128000).default(2048),
  api_key_id: z.string().uuid().optional().nullable(),
}).refine(
  (data) => {
    // If model is set, provider must also be set
    if (data.model && !data.provider) {
      return false
    }
    return true
  },
  {
    message: i18n.t('validation:provider.required'),
    path: ['provider'],
  }
)

// Static schema for backwards compatibility
export const stepLLMConfigSchema = getStepLLMConfigSchema()

export type StepLLMConfigData = z.infer<typeof stepLLMConfigSchema>

// Step 3: System Prompt Schema

// Factory function to get step system prompt schema with current translations
export const getStepSystemPromptSchema = () => z.object({
  system_prompt: z
    .string()
    .max(100000, i18n.t('validation:prompt.maxLength', { max: 100000 }))
    .optional()
    .nullable(),
})

// Static schema for backwards compatibility
export const stepSystemPromptSchema = getStepSystemPromptSchema()

export type StepSystemPromptData = z.infer<typeof stepSystemPromptSchema>

// Step 4: Tools Schema
// Note: tool_ids accepts any string since IDs can come from different sources
// (demo tools with string IDs like 'demo-web-search', or backend UUIDs)
// tool_configs stores per-tool configuration (e.g., folder_id for Google Drive)
export const stepToolsSchema = z.object({
  tool_ids: z.array(z.string()).default([]),
  tool_configs: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
})

export type StepToolsData = z.infer<typeof stepToolsSchema>
export type ToolConfigs = Record<string, Record<string, unknown>>

// Step 5: Knowledge Bases Schema
// Note: knowledge_base_ids accepts any string since IDs can come from different sources
// (demo KBs with string IDs like 'demo-kb-1', or backend UUIDs)
export const stepKnowledgeBasesSchema = z.object({
  knowledge_base_ids: z.array(z.string()).default([]),
})

export type StepKnowledgeBasesData = z.infer<typeof stepKnowledgeBasesSchema>

// Step 6: Advanced Settings Schema
export const stepAdvancedSchema = z.object({
  // Memory config
  memory_enabled: z.boolean().default(true),
  memory_type: z.string().optional().nullable().default('short_term'),
  memory_scope: z.string().optional().nullable().default('conversation'),
  memory_context_window: z.number().min(1).max(100).default(20),
  memory_retention_days: z.number().min(1).optional().nullable(),
  memory_auto_summarize: z.boolean().default(false),
  // Legacy memory fields (for backward compatibility)
  memory_max_tokens: z.number().min(1).max(128000).optional().nullable(),
  memory_window_size: z.number().min(1).max(1000).optional().nullable(),
  // Execution settings
  timeout: z.number().min(1).max(600).default(60),
  max_retries: z.number().min(0).max(10).default(3),
  max_iterations: z.number().min(1).max(100).default(10),
  // Template and status
  is_template: z.boolean().default(false),
  is_active: z.boolean().default(false),
})

export type StepAdvancedData = z.infer<typeof stepAdvancedSchema>

// Step 0: Template Selection Schema (optional step)
export const stepTemplateSelectSchema = z.object({
  selected_template_id: z.string().uuid().optional().nullable(),
})

export type StepTemplateSelectData = z.infer<typeof stepTemplateSelectSchema>

// Complete wizard schema (all steps combined)
export const agentWizardSchema = z.object({
  // Step 0 (Template Selection)
  selected_template_id: stepTemplateSelectSchema.shape.selected_template_id,
  // Step 1
  name: stepBasicInfoSchema.shape.name,
  description: stepBasicInfoSchema.shape.description,
  icon: stepBasicInfoSchema.shape.icon,
  // Step 2
  provider: stepLLMConfigSchema.innerType().shape.provider,
  model: stepLLMConfigSchema.innerType().shape.model,
  temperature: stepLLMConfigSchema.innerType().shape.temperature,
  max_tokens: stepLLMConfigSchema.innerType().shape.max_tokens,
  api_key_id: stepLLMConfigSchema.innerType().shape.api_key_id,
  // Step 3
  system_prompt: stepSystemPromptSchema.shape.system_prompt,
  // Step 4
  tool_ids: stepToolsSchema.shape.tool_ids,
  tool_configs: stepToolsSchema.shape.tool_configs,
  // Step 5
  knowledge_base_ids: stepKnowledgeBasesSchema.shape.knowledge_base_ids,
  // Step 6
  memory_enabled: stepAdvancedSchema.shape.memory_enabled,
  memory_type: stepAdvancedSchema.shape.memory_type,
  memory_scope: stepAdvancedSchema.shape.memory_scope,
  memory_context_window: stepAdvancedSchema.shape.memory_context_window,
  memory_retention_days: stepAdvancedSchema.shape.memory_retention_days,
  memory_auto_summarize: stepAdvancedSchema.shape.memory_auto_summarize,
  memory_max_tokens: stepAdvancedSchema.shape.memory_max_tokens,
  memory_window_size: stepAdvancedSchema.shape.memory_window_size,
  timeout: stepAdvancedSchema.shape.timeout,
  max_retries: stepAdvancedSchema.shape.max_retries,
  max_iterations: stepAdvancedSchema.shape.max_iterations,
  is_template: stepAdvancedSchema.shape.is_template,
  is_active: stepAdvancedSchema.shape.is_active,
})

export type AgentWizardData = z.infer<typeof agentWizardSchema>

// Default values for the wizard
export const agentWizardDefaults: AgentWizardData = {
  selected_template_id: null,
  name: '',
  description: null,
  icon: '🤖',
  provider: null,
  model: null,
  temperature: 0.7,
  max_tokens: 2048,
  api_key_id: null,
  system_prompt: null,
  tool_ids: [],
  tool_configs: {},
  knowledge_base_ids: [],
  memory_enabled: true,
  memory_type: 'short_term',
  memory_scope: 'conversation',
  memory_context_window: 20,
  memory_retention_days: null,
  memory_auto_summarize: false,
  memory_max_tokens: null,
  memory_window_size: null,
  timeout: 60,
  max_retries: 3,
  max_iterations: 10,
  is_template: false,
  is_active: false,
}

// Wizard steps configuration
export const WIZARD_STEPS = [
  {
    id: 0,
    title: 'Template',
    description: 'Escolha um template ou comece do zero',
    fields: ['selected_template_id'],
    schema: stepTemplateSelectSchema,
    optional: true,
  },
  {
    id: 1,
    title: 'Informações Básicas',
    description: 'Nome e descrição do agente',
    fields: ['name', 'description', 'icon'],
    schema: stepBasicInfoSchema,
    optional: false,
  },
  {
    id: 2,
    title: 'Configuração LLM',
    description: 'Modelo de linguagem e parâmetros',
    fields: ['provider', 'model', 'temperature', 'max_tokens', 'api_key_id'],
    schema: stepLLMConfigSchema,
    optional: false,
  },
  {
    id: 3,
    title: 'System Prompt',
    description: 'Instruções e comportamento',
    fields: ['system_prompt'],
    schema: stepSystemPromptSchema,
    optional: false,
  },
  {
    id: 4,
    title: 'Ferramentas',
    description: 'Tools disponíveis para o agente',
    fields: ['tool_ids', 'tool_configs'],
    schema: stepToolsSchema,
    optional: false,
  },
  {
    id: 5,
    title: 'Knowledge Base',
    description: 'Bases de conhecimento',
    fields: ['knowledge_base_ids'],
    schema: stepKnowledgeBasesSchema,
    optional: false,
  },
  {
    id: 6,
    title: 'Configurações Avançadas',
    description: 'Memória e execução',
    fields: ['memory_enabled', 'memory_type', 'memory_scope', 'memory_context_window', 'memory_retention_days', 'memory_auto_summarize', 'memory_max_tokens', 'memory_window_size', 'timeout', 'max_retries', 'max_iterations', 'is_template', 'is_active'],
    schema: stepAdvancedSchema,
    optional: false,
  },
  {
    // O PASSO DO MÓDULO. Só aparece quando a chave "Agente de atendimento
    // conversacional" está ligada — e é `optional` porque o documento do
    // escritório NÃO é campo do formulário do agente: ele tem versões, gate de
    // publicação e vida própria. Validar o passo aqui misturaria dois ciclos.
    id: 7,
    title: 'Atendimento',
    description: 'O documento que descreve o comportamento do assistente',
    fields: [],
    schema: z.object({}),
    optional: true,
  },
] as const

export type WizardStep = (typeof WIZARD_STEPS)[number]

// Helper to get fields for a specific step
export function getStepFields(stepId: number): readonly string[] {
  const step = WIZARD_STEPS.find((s) => s.id === stepId)
  return step?.fields ?? []
}

// Helper to get schema for a specific step
export function getStepSchema(stepId: number) {
  const step = WIZARD_STEPS.find((s) => s.id === stepId)
  return step?.schema
}
